import React from 'react';
import commonDefinitions from 'vets-json-schema/dist/definitions.json';
import VaMemorableDateField from '../web-component-fields/VaMemorableDateField';
import VaDateField from '../web-component-fields/VaDateField';
import { minYear, currentYear, maxYear, formatPartialDate } from '../helpers';
import {
  validateCurrentOrPastMemorableDate,
  validateCurrentOrPastMonthYear,
  validateCurrentOrFutureMemorableDate,
  validateCurrentOrFutureMonthYear,
  validateAnyMemorableDate,
  validateAnyMonthYear,
  validateDateRange,
} from '../validation';

/**
 * Groups each date rule's validation, messages and year range.
 */
const DATE_RULES = {
  currentOrPast: {
    validation: validateCurrentOrPastMemorableDate,
    monthYearValidation: validateCurrentOrPastMonthYear,
    errorKey: 'futureDate',
    error: 'Provide a valid current or past date',
    patternError: 'Enter a valid current or past date',
    minYear,
    maxYear: currentYear,
  },
  currentOrFuture: {
    validation: validateCurrentOrFutureMemorableDate,
    monthYearValidation: validateCurrentOrFutureMonthYear,
    errorKey: 'pastDate',
    error: 'Provide a valid current or future date',
    patternError: 'Enter a valid current or future date',
    minYear: currentYear,
    maxYear,
  },
  any: {
    validation: validateAnyMemorableDate,
    monthYearValidation: validateAnyMonthYear,
    patternError: 'Enter a valid date',
    minYear,
    maxYear,
  },
};

const ALLOWED_DATES = Object.keys(DATE_RULES);

const buildDateUI = (options, rule) => {
  const {
    title,
    errorMessages,
    required,
    dataDogHidden = false,
    validations,
    ...uiOptions
  } = typeof options === 'object' ? options : { title: options };

  // currentOrPastDateSchema needs a day, so monthYearOnly there needs
  // currentOrPastMonthYearDateSchema. dateSchema takes both.
  const monthYearOnly = uiOptions?.monthYearOnly;

  const uiTitle = title ?? 'Date';
  const localeDateStringOptions = monthYearOnly
    ? { year: 'numeric', month: 'long' }
    : { year: 'numeric', month: 'long', day: 'numeric' };

  const ruleValidation = monthYearOnly
    ? rule.monthYearValidation
    : rule.validation;
  const allValidations = [ruleValidation, ...(validations || [])];

  return {
    'ui:title': uiTitle,
    'ui:webComponentField': monthYearOnly ? VaDateField : VaMemorableDateField,
    'ui:required': required,
    'ui:validations': allValidations,
    'ui:errorMessages': {
      pattern: errorMessages?.pattern || rule.patternError,
      required: errorMessages?.required || 'Enter a date',
      ...(rule.errorKey && {
        [rule.errorKey]: errorMessages?.[rule.errorKey] || rule.error,
      }),
      ...(uiOptions.customYearErrorMessage && {
        year: uiOptions.customYearErrorMessage,
      }),
    },
    'ui:options': {
      ...uiOptions,
      minYear: rule.minYear,
      maxYear: rule.maxYear,
    },
    'ui:reviewField': ({ children }) => (
      <div className="review-row">
        <dt>{uiTitle}</dt>
        <dd
          className={dataDogHidden ? 'dd-privacy-hidden' : undefined}
          data-dd-action-name={dataDogHidden ? uiTitle : undefined}
        >
          {children.props.formData && (
            <>
              {formatPartialDate(
                children.props.formData,
                localeDateStringOptions,
              )}
            </>
          )}
        </dd>
      </div>
    ),
  };
};

const buildAllowedDatesUI = (options, patternName) => {
  const { allowedDates, errorMessages, ...fieldOptions } = options ?? {};

  if (!allowedDates) {
    throw new Error(
      `${patternName} needs allowedDates. Use one of: ${ALLOWED_DATES.join(
        ', ',
      )}.`,
    );
  }

  if (!ALLOWED_DATES.includes(allowedDates)) {
    throw new Error(
      `"${allowedDates}" is not a valid allowedDates value for ${patternName}. Use one of: ${ALLOWED_DATES.join(
        ', ',
      )}.`,
    );
  }

  const rule = DATE_RULES[allowedDates];
  const { allowedDates: allowedDatesMessage, ...messages } =
    errorMessages ?? {};

  if (!rule.errorKey && allowedDatesMessage) {
    throw new Error(
      `${patternName} with allowedDates "${allowedDates}" does not take an allowedDates error message, because no dates are ruled out. Use errorMessages.pattern instead.`,
    );
  }

  // allowedDates is a message for whichever key the rule uses, not a key itself
  return buildDateUI(
    {
      ...fieldOptions,
      errorMessages: rule.errorKey
        ? { [rule.errorKey]: allowedDatesMessage, ...messages }
        : messages,
    },
    rule,
  );
};

/**
 * Date field uiSchema for the dates `allowedDates` permits.
 * `allowedDates` is required and sets the validation, error messages and year
 * range together.
 *
 * ```js
 * exampleDate: dateUI({ title: 'Date of event', allowedDates: 'currentOrPast' })
 *
 * // A date that has not happened yet:
 * exampleDate: dateUI({
 *   title: 'Expected graduation date',
 *   allowedDates: 'currentOrFuture',
 * })
 *
 * // Any date:
 * exampleDate: dateUI({ title: 'Date of event', allowedDates: 'any' })
 *
 * // Month and year only:
 * exampleDate: dateUI({
 *   title: 'Start month',
 *   allowedDates: 'currentOrPast',
 *   monthYearOnly: true,
 * })
 * ```
 *
 * `validations` stack on top of the date rule:
 *
 * ```js
 * exampleDate: dateUI({
 *   title: 'Date of divorce',
 *   allowedDates: 'currentOrPast',
 *   validations: [
 *     (errors, date, formData) => {
 *       if (isBefore(parseISO(date), parseISO(formData.marriageDate))) {
 *         errors.addError('Enter a date on or after your marriage date');
 *       }
 *     },
 *   ],
 * })
 * ```
 *
 * `errorMessages.allowedDates` rewords the out of range message (not with `any`).
 *
 * ```js
 * exampleDate: dateUI({
 *   title: 'Date of separation',
 *   allowedDates: 'currentOrPast',
 *   errorMessages: {
 *     required: 'Enter your separation date',
 *     allowedDates: 'Enter a date that has already happened',
 *   },
 * })
 * ```
 * @param {UIOptions & {
 *   title?: UISchemaOptions['ui:title'],
 *   hint?: string,
 *   allowedDates: 'currentOrPast' | 'currentOrFuture' | 'any',
 *   errorMessages?: UISchemaOptions['ui:errorMessages'] & { allowedDates?: string },
 *   customYearErrorMessage?: string,
 *   dataDogHidden?: boolean,
 *   monthSelect?: boolean,
 *   monthYearOnly?: boolean,
 *   monthOptional?: boolean, // only applies alongside monthYearOnly
 *   removeDateHint?: boolean,
 *   validations?: UISchemaOptions['ui:validations'],
 * }} options
 * @returns {UISchemaOptions} uiSchema
 */
const dateUI = options => buildAllowedDatesUI(options, 'dateUI');

/**
 * uiSchema for a generic current or past date
 *
 * ```js
 * exampleDate: currentOrPastDateUI('Date of event')
 * exampleDate: currentOrPastDateUI({
 *  title: 'Date of event',
 *  hint: 'This is a hint'
 * })
 * exampleDate: {
 *  ...currentOrPastDateUI('Date of event')
 * }
 * ```
 * @param {string | UIOptions & {
 *   title?: UISchemaOptions['ui:title'],
 *   hint?: string,
 *   errorMessages?: UISchemaOptions['ui:errorMessages'] & { futureDate?: string },
 *   dataDogHidden?: boolean,
 *   monthSelect?: boolean,
 *   monthYearOnly?: boolean,
 *   monthOptional?: boolean, // only applies alongside monthYearOnly
 *   removeDateHint?: boolean,
 *   validations?: UISchemaOptions['ui:validations'],
 * }} [options] accepts a single string for title, or an object of options
 * @returns {UISchemaOptions} uiSchema
 */
const currentOrPastDateUI = options => {
  if (options?.allowedDates) {
    throw new Error(
      'This date pattern sets its own allowed dates. Use dateUI or dateRangeUI to choose them.',
    );
  }
  return buildDateUI(options, DATE_RULES.currentOrPast);
};

/**
 * uiSchema for a current or past month and year
 *
 * ```js
 * exampleDate: currentOrPastDateUI('Date of event')
 * exampleDate: currentOrPastDateUI({
 *  title: 'Date of event',
 *  hint: 'This is a hint'
 * })
 * exampleDate: {
 *  ...currentOrPastDateUI('Date of event')
 * }
 * ```
 *
 * Set `monthOptional` to accept a year on its own, stored as `YYYY-XX`:
 *
 * ```js
 * exampleDate: currentOrPastMonthYearDateUI({
 *  title: 'Date of event',
 *  monthOptional: true
 * })
 * ```
 * @param {string | UIOptions & {
 *   title?: UISchemaOptions['ui:title'],
 *   hint?: string,
 *   errorMessages?: UISchemaOptions['ui:errorMessages'],
 *   monthOptional?: boolean,
 *   removeDateHint?: boolean,
 *   dataDogHidden?: boolean,
 *   validations?: UISchemaOptions['ui:validations'],
 * }} [options] accepts a single string for title, or an object of options
 * @returns {UISchemaOptions} uiSchema
 */
const currentOrPastMonthYearDateUI = options => {
  return currentOrPastDateUI({
    ...(typeof options === 'string' ? { title: options } : options),
    monthYearOnly: true,
  });
};

const buildDateRangeUI = (fromOptions, toOptions, errorMessage, buildField) => {
  let fromLabel = 'From date';
  let toLabel = 'To date';
  let fromCustomOptions = {};
  let toCustomOptions = {};

  if (typeof fromOptions === 'object') {
    fromLabel = fromOptions.title || fromLabel;
    fromCustomOptions = { ...fromOptions };
  } else {
    fromLabel = fromOptions || fromLabel;
  }

  if (typeof toOptions === 'object') {
    toLabel = toOptions.title || toLabel;
    toCustomOptions = { ...toOptions };
  } else {
    toLabel = toOptions || toLabel;
  }

  return {
    'ui:validations': [
      {
        validator: (errors, fieldData, formData, schema, uiSchema) => {
          validateDateRange(
            errors,
            fieldData,
            formData,
            schema,
            uiSchema,
            true,
          );
        },
      },
    ],
    from: buildField({ title: fromLabel, ...fromCustomOptions }),
    to: buildField({ title: toLabel, ...toCustomOptions }),
    ...(errorMessage && { 'ui:errorMessages': { pattern: errorMessage } }),
  };
};

/**
 * Date range uiSchema with "from" and "to" date fields.
 * Each field takes the same options as `dateUI`, so one range can mix rules.
 *
 * ```js
 * // A range that has started but has not finished:
 * exampleDateRange: dateRangeUI(
 *   { title: 'Service start date', allowedDates: 'currentOrPast' },
 *   { title: 'Expected end date', allowedDates: 'currentOrFuture' },
 * )
 *
 * // Month and year only. Set monthYearOnly on both fields:
 * exampleMonthRange: dateRangeUI(
 *   { title: 'Start month', allowedDates: 'currentOrPast', monthYearOnly: true },
 *   { title: 'End month', allowedDates: 'any', monthYearOnly: true },
 * )
 * ```
 * @param {UIOptions & {
 *   title?: UISchemaOptions['ui:title'],
 *   hint?: string,
 *   allowedDates: 'currentOrPast' | 'currentOrFuture' | 'any',
 *   errorMessages?: UISchemaOptions['ui:errorMessages'] & { allowedDates?: string },
 *   customYearErrorMessage?: string,
 *   dataDogHidden?: boolean,
 *   monthSelect?: boolean,
 *   monthYearOnly?: boolean,
 *   monthOptional?: boolean, // only applies alongside monthYearOnly
 *   removeDateHint?: boolean,
 *   validations?: UISchemaOptions['ui:validations'],
 * }} fromOptions
 * @param {UIOptions & {
 *   title?: UISchemaOptions['ui:title'],
 *   hint?: string,
 *   allowedDates: 'currentOrPast' | 'currentOrFuture' | 'any',
 *   errorMessages?: UISchemaOptions['ui:errorMessages'] & { allowedDates?: string },
 *   customYearErrorMessage?: string,
 *   dataDogHidden?: boolean,
 *   monthSelect?: boolean,
 *   monthYearOnly?: boolean,
 *   monthOptional?: boolean, // only applies alongside monthYearOnly
 *   removeDateHint?: boolean,
 *   validations?: UISchemaOptions['ui:validations'],
 * }} toOptions
 * @param {string} [errorMessage] - custom range error message
 * @returns {UISchemaOptions} uiSchema
 */
const dateRangeUI = (fromOptions, toOptions, errorMessage) => {
  if (!!fromOptions?.monthYearOnly !== !!toOptions?.monthYearOnly) {
    throw new Error(
      'dateRangeUI needs monthYearOnly on both fields or neither, so the range compares like dates.',
    );
  }
  return buildDateRangeUI(fromOptions, toOptions, errorMessage, fieldOptions =>
    buildAllowedDatesUI(fieldOptions, 'dateRangeUI'),
  );
};

/**
 * uiSchema for a "from" and "to" date range with current or past dates. Includes two fields.
 *
 * ```js
 * // Simple usage:
 * exampleDateRange: currentOrPastDateRangeUI(
 *   'Start date of event',
 *   'End date of event',
 *   'Custom error message'
 * )
 *
 * // Advanced usage:
 * exampleDateRange: currentOrPastDateRangeUI(
 *   { title: 'Start date of event', ... },
 *   { title: 'End date of event', hint: 'This is a hint' },
 *   'Custom error message'
 * })
 * ```
 * @param {string | UIOptions & {
 *   title?: UISchemaOptions['ui:title'],
 *   hint?: string,
 *   errorMessages?: UISchemaOptions['ui:errorMessages'],
 *   dataDogHidden?: boolean,
 *   removeDateHint?: boolean,
 *   validations?: UISchemaOptions['ui:validations'],
 * }} [fromOptions] accepts a single string for start/from date title, or an object of options
 * @param {string | UIOptions & {
 *   title?: UISchemaOptions['ui:title'],
 *   hint?: string,
 *   errorMessages?: UISchemaOptions['ui:errorMessages'],
 *   dataDogHidden?: boolean,
 *   removeDateHint?: boolean,
 *   validations?: UISchemaOptions['ui:validations'],
 * }} [toOptions] accepts a single string for to/end date title, or an object of options
 * @param {string} [errorMessage] - Optional custom error message for the date range validation
 * @returns {UISchemaOptions} uiSchema
 */
const currentOrPastDateRangeUI = (fromOptions, toOptions, errorMessage) =>
  buildDateRangeUI(fromOptions, toOptions, errorMessage, currentOrPastDateUI);

/**
 * uiSchema for month and year only date range. Includes two fields for "from" and "to" dates.
 *
 * ```js
 * // Simple usage:
 * exampleDateRange: currentOrPastDateRangeUI(
 *   'Start date of event',
 *   'End date of event',
 *   'Custom error message'
 * )
 *
 * // Advanced usage:
 * exampleDateRange: currentOrPastDateRangeUI(
 *   { title: 'Start date of event', ... },
 *   { title: 'End date of event', hint: 'This is a hint' },
 *   'Custom error message'
 * })
 * ```
 * @param {string | UIOptions & {
 *   title?: UISchemaOptions['ui:title'],
 *   hint?: string,
 *   errorMessages?: UISchemaOptions['ui:errorMessages'],
 *   dataDogHidden?: boolean,
 *   removeDateHint?: boolean,
 *   validations?: UISchemaOptions['ui:validations'],
 * }} [fromOptions] accepts a single string for start/from date title, or an object of options
 * @param {string | UIOptions & {
 *   title?: UISchemaOptions['ui:title'],
 *   hint?: string,
 *   errorMessages?: UISchemaOptions['ui:errorMessages'],
 *   dataDogHidden?: boolean,
 *   removeDateHint?: boolean,
 *   validations?: UISchemaOptions['ui:validations'],
 * }} [toOptions] accepts a single string for to/end date title, or an object of options
 * @param {string} [errorMessage] - Optional custom error message for the date range validation
 * @returns {UISchemaOptions} uiSchema
 */
const currentOrPastMonthYearDateRangeUI = (
  fromOptions,
  toOptions,
  errorMessage,
) => {
  return currentOrPastDateRangeUI(
    {
      ...(typeof fromOptions === 'string'
        ? { title: fromOptions }
        : fromOptions),
      monthYearOnly: true,
    },
    {
      ...(typeof toOptions === 'string' ? { title: toOptions } : toOptions),
      monthYearOnly: true,
    },
    errorMessage,
  );
};

/**
 * uiSchema for current or past dates with digit select for month
 *
 * ```js
 * exampleDate: currentOrPastDateDigitsUI('Date of event')
 * exampleDate: currentOrPastDateDigitsUI({
 *  title: 'Date of event',
 *  hint: 'This is a hint'
 * })
 * exampleDate: {
 *  ...currentOrPastDateDigitsUI('Date of event')
 * }
 * ```
 * @param {string | UIOptions & {
 *   title?: UISchemaOptions['ui:title'],
 *   hint?: string,
 *   monthYearOnly?: boolean,
 *   removeDateHint?: boolean,
 *   errorMessages?: UISchemaOptions['ui:errorMessages'],
 *   validations?: UISchemaOptions['ui:validations'],
 * }} [options] accepts a single string for title, or an object of options
 * @returns {UISchemaOptions} uiSchema
 */
const currentOrPastDateDigitsUI = options => {
  const { title, ...uiOptions } =
    typeof options === 'object' ? options : { title: options };

  return currentOrPastDateUI({
    title,
    monthSelect: false,
    ...uiOptions,
  });
};

/**
 * uiSchema for date of birth
 *
 * ```js
 * dateOfBirth: dateOfBirthUI()
 * dateOfBirth: dateOfBirthUI('Stepchild’s date of birth')
 * dateOfBirth: dateOfBirthUI({
 *    title: 'Stepchild’s date of birth',
 *    hint: 'This is a hint'
 * })
 * ```
 * @param {string | UIOptions & {
 *   title?: UISchemaOptions['ui:title'],
 *   hint?: string,
 *   monthYearOnly?: boolean,
 *   removeDateHint?: boolean,
 *   errorMessages?: UISchemaOptions['ui:errorMessages'],
 *   validations?: UISchemaOptions['ui:validations'],
 * }} [options] accepts a single string for title, or an object of options
 * @returns {UISchemaOptions} uiSchema
 */
const dateOfBirthUI = options => {
  const { title, errorMessages, ...uiOptions } =
    typeof options === 'object' ? options : { title: options };

  return currentOrPastDateUI({
    title: title || 'Date of birth',
    errorMessages: errorMessages || {
      pattern: 'Provide a valid date',
      required: 'Enter the date of birth',
    },
    ...uiOptions,
  });
};

/**
 * uiSchema for date of death
 *
 * ```js
 * dateOfDeath: dateOfDeathUI()
 * dateOfDeath: dateOfDeathUI('Stepchild’s date of death')
 * dateOfBirth: dateOfDeathUI({
 *    title: 'Stepchild’s date of death',
 *    hint: 'This is a hint'
 * })
 * ```
 * @param {string | UIOptions & {
 *   title?: UISchemaOptions['ui:title'],
 *   hint?: string,
 *   monthYearOnly?: boolean,
 *   removeDateHint?: boolean,
 *   errorMessages?: UISchemaOptions['ui:errorMessages'],
 *   validations?: UISchemaOptions['ui:validations'],
 * }} [options] accepts a single string for title, or an object of options
 * @returns {UISchemaOptions} uiSchema
 */
const dateOfDeathUI = options => {
  const { title, errorMessages, ...uiOptions } =
    typeof options === 'object' ? options : { title: options };

  return currentOrPastDateUI({
    title: title || 'Date of death',
    errorMessages: errorMessages || {
      pattern: 'Provide a valid date',
      required: 'Enter the date of death',
    },
    ...uiOptions,
  });
};

/**
 * @returns `commonDefinitions.date`
 */
const currentOrPastDateSchema = commonDefinitions.date;

const monthYearDateSchema = {
  type: 'string',
  pattern: '^(\\d{4}|XXXX)-(0[1-9]|1[0-2]|XX)$',
};

/**
 * Schema for currentOrPastMonthYearDateUI
 */
const currentOrPastMonthYearDateSchema = monthYearDateSchema;

/**
 * @returns `commonDefinitions.date`
 */
const currentOrPastDateDigitsSchema = commonDefinitions.date;

/**
 * @returns `commonDefinitions.date`
 */
const dateOfBirthSchema = commonDefinitions.date;

/**
 * @returns `commonDefinitions.date`
 */
const dateOfDeathSchema = commonDefinitions.date;

/**
 * Schema for currentOrPastDateRangeUI
 *
 * Usage:
 * ```
 * dateRange: currentOrPastDateRangeSchema
 * dateRange: {
 *   ...currentOrPastDateRangeSchema
 *   required: []
 * }
 * ```
 */
const currentOrPastDateRangeSchema = {
  type: 'object',
  properties: {
    from: currentOrPastDateSchema,
    to: currentOrPastDateSchema,
  },
  required: ['from', 'to'],
};

/**
 * Schema for currentOrPastMonthYearDateRangeUI
 *
 * Usage:
 * ```
 * dateRange: currentOrPastMonthYearDateRangeSchema
 * dateRange: {
 *   ...currentOrPastMonthYearDateRangeSchema
 *   required: []
 * }
 * ```
 */
const currentOrPastMonthYearDateRangeSchema = {
  type: 'object',
  properties: {
    from: currentOrPastMonthYearDateSchema,
    to: currentOrPastMonthYearDateSchema,
  },
  required: ['from', 'to'],
};

/**
 * Schema for dateUI, full dates or month and year.
 * monthYearOnly goes on the uiSchema only.
 *
 * ```js
 * exampleDate: dateSchema
 * ```
 */
const dateSchema = {
  type: 'string',
  pattern:
    '^(\\d{4}|XXXX)-(0[1-9]|1[0-2]|XX)(-(0[1-9]|[1-2][0-9]|3[0-1]|XX))?$',
};

/**
 * Schema for dateRangeUI
 *
 * ```js
 * exampleDateRange: dateRangeSchema
 * exampleDateRange: {
 *   ...dateRangeSchema,
 *   required: [],
 * }
 * ```
 */
const dateRangeSchema = {
  type: 'object',
  properties: {
    from: dateSchema,
    to: dateSchema,
  },
  required: ['from', 'to'],
};

export {
  dateUI,
  dateRangeUI,
  dateSchema,
  dateRangeSchema,
  currentOrPastDateUI,
  currentOrPastDateDigitsUI,
  currentOrPastDateRangeUI,
  currentOrPastMonthYearDateUI,
  currentOrPastMonthYearDateRangeUI,
  dateOfBirthUI,
  dateOfDeathUI,
  currentOrPastDateRangeSchema,
  currentOrPastDateSchema,
  currentOrPastDateDigitsSchema,
  currentOrPastMonthYearDateSchema,
  currentOrPastMonthYearDateRangeSchema,
  dateOfBirthSchema,
  dateOfDeathSchema,
};
